<template>
  <footer class="site-footer">
    <div class="footer-container">
      <!-- Left Section: Logo and Contact -->
      <div 
        class="footer-left" 
        :class="{ 'animate-in': isVisible }"
        ref="footerLeft"
      >
        <div class="school-logo">
          <div class="logo-icon">
           <img :src="logoSrc" width="60px" alt="">
          </div>
          <div class="logo-text">
            <span class="school-name">{{ footer_text || 'ALSHOLA American SCHOOL' }}</span>
          </div>
        </div>
        
        <div class="contact-info">
          <p>{{ address1 }}</p>
          <p>{{ address2 }}</p>
          <p>{{ address3 }}</p>
          <p class="phone" @click="callPhone">{{ phone }}</p>
        </div>

        <div class="social-icons">
          <a 
            v-for="social in socialLinks" 
            :key="social.name"
            :href="social.url" 
            :title="social.tooltip"
            target="_blank"
            class="social-icon"
          >
            <i :class="social.icon"></i>
          </a>
        </div>
      </div>

   

      <!-- Right Section: Map -->
      <div 
        class="footer-right"
        :class="{ 'animate-in': isVisible }"
        ref="footerRight"
      >
        <div 
          class="map-container"
          @mouseenter="scaleMap"
          @mouseleave="resetMap"
        >
          <iframe 
            v-if="mapLoaded"
            :src="mapUrl"
            class="map-iframe"
            style="border:0;" 
            allowfullscreen="" 
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
          <div v-else class="map-placeholder">
            <i class="fas fa-map-marker-alt"></i>
            <p>Loading map...</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="footer-bottom">
      <div class="bottom-container">
        <p class="copyright">{{ copyrightText }}</p>
        <!-- <a href="#" class="terms-link">Terms & Privacy</a>
        <p class="credits"> powered by <strong>INNO SOURCE</strong></p> -->
      </div>
    </div>

    <!-- Ripple Effect Element -->
    <span v-if="showRipple" class="ripple" :style="rippleStyle"></span>
  </footer>
</template>

<script>

import { siteSettings, loadSettings } from '@/services/settings';

export default {
  name: 'SchoolFooter',
  data() {
    return {
      isVisible: false,
      mapLoaded: false,
      showRipple: false,
      rippleStyle: {},
      currentYear: new Date().getFullYear(),
      settings: siteSettings,
      fallbackLogo: require('../assets/logo2.png'),
      navButtons: [
        { text: 'INQUIRE', link: '#inquire', active: false },
        { text: 'VISIT', link: '#visit', active: false },
        { text: 'APPLY', link: '#apply', active: false }
      ],
      usefulLinks: [
        { text: 'Admissions', url: '/sasadmission' },
        { text: 'Fees', url: '/fees' },
        { text: 'Tours', url: '/tours' },
        { text: 'Contact', url: '/Contact' }
      ],
      socialLinks: [
        { name: 'Facebook', url: '#', icon: 'fab fa-facebook-f', tooltip: 'Follow us on Facebook' },
        { name: 'Twitter', url: '#', icon: 'fab fa-twitter', tooltip: 'Follow us on Twitter' },
        { name: 'Instagram', url: '#', icon: 'fab fa-instagram', tooltip: 'Follow us on Instagram' },
        { name: 'YouTube', url: '#', icon: 'fab fa-youtube', tooltip: 'Subscribe to our YouTube channel' },
        { name: 'LinkedIn', url: '#', icon: 'fab fa-linkedin', tooltip: 'Follow us on LinkedIn' }
      ],
    };
  },
  computed: {
    address1() { return this.settings.address_line_1 || 'Almwihat Street'; },
    address2() { return this.settings.address_line_2 || 'Al-Talla 2'; },
    address3() { return this.settings.city || 'Ajman, U.A.E.'; },
    phone() {
      const parts = [];
      if (this.settings.phone_primary) parts.push(this.settings.phone_primary);
      if (this.settings.phone_secondary) parts.push(this.settings.phone_secondary);
      return 'Tel: ' + (parts.join(' | ') || '+971 54 7041499 | +971 6 7451110');
    },
    mapUrl() {
      const url = this.settings.map_embed_url || this.settings.google_maps_embed_url || '';
      if (url && !url.includes('/embed')) {
        return url.replace('www.google.com/maps', 'www.google.com/maps/embed');
      }
      return url || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3613.2!2d55.2089!3d25.1304';
    },
    copyrightText() {
      const raw = this.settings.copyright_text || 'Copyright © ' + this.currentYear + ' Al Shola PVT. All Rights Reserved';
      return raw.replace('{{ currentYear }}', this.currentYear).replace('{{currentYear}}', this.currentYear);
    },
    logoSrc() { return this.settings.logo || this.fallbackLogo; },
    footer_text() {
      return this.settings.footer_text || 'ALSHOLA INTERNATIONAL SCHOOL';
    }
  },
  async created() {
    await loadSettings();
    if (this.settings.facebook_url) this.socialLinks[0].url = this.settings.facebook_url;
    if (this.settings.twitter_url) this.socialLinks[1].url = this.settings.twitter_url;
    if (this.settings.instagram_url) this.socialLinks[2].url = this.settings.instagram_url;
    if (this.settings.youtube_url) this.socialLinks[3].url = this.settings.youtube_url;
    if (this.settings.linkedin_url) this.socialLinks[4].url = this.settings.linkedin_url;
  },
  mounted() {
    this.setupIntersectionObserver();
    this.loadMapOnScroll();
    this.setActiveNav();
  },
  methods: {
    setupIntersectionObserver() {
      const options = { threshold: 0.1, rootMargin: '0px 0px -50px 0px' };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => { if (entry.isIntersecting) { this.isVisible = true; } });
      }, options);
      if (this.$refs.footerLeft) { observer.observe(this.$refs.footerLeft); }
    },
    loadMapOnScroll() {
      const loadMap = () => {
        const mapContainer = this.$el?.querySelector('.map-container');
        if (mapContainer && mapContainer.getBoundingClientRect().top < window.innerHeight) {
          this.mapLoaded = true;
          window.removeEventListener('scroll', loadMap);
        }
      };
      window.addEventListener('scroll', loadMap);
      this.$nextTick(() => { loadMap(); });
    },
    setActiveNav() {
      const currentHash = window.location.hash;
      this.navButtons.forEach(btn => { btn.active = btn.link === currentHash; });
    },
    handleNavClick(event, button) {
      this.createRipple(event);
      this.navButtons.forEach(btn => { btn.active = btn.text === button.text; });
      this.$emit('nav-click', button);
    },
    createRipple(event) {
      const element = event.currentTarget;
      const rect = element.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;
      this.rippleStyle = { width: size+'px', height: size+'px', left: x+'px', top: y+'px' };
      this.showRipple = true;
      setTimeout(() => { this.showRipple = false; }, 600);
    },
    callPhone() { window.location.href = 'tel:' + (this.settings.phone_primary || '+97144271400'); },
    scaleMap(event) { event.currentTarget.style.transform = 'scale(1.02)'; event.currentTarget.style.transition = 'transform 0.3s ease'; },
    resetMap(event) { event.currentTarget.style.transform = 'scale(1)'; }
  },
  beforeUnmount() { window.removeEventListener('scroll', this.loadMapOnScroll); }
};

</script>

<style scoped>
/* Footer Styles */
.site-footer {
  background: linear-gradient(135deg, #c02131c9 0%, #f7f02bb9 100%);
  color: white;
  padding: 50px 0 0;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto 30px;
  padding: 0 30px;

  display: flex;
  gap: 32px;
  align-items: flex-start;
}

/* Animation */
.footer-left,
.footer-middle,
.footer-right {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.footer-left.animate-in,
.footer-middle.animate-in,
.footer-right.animate-in {
  opacity: 1;
  transform: translateY(0);
}

/* Left Section */
.footer-left {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 25px;
}

.school-logo {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo-icon {
  width: 60px;
  height: 60px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.logo-icon::before {
  content: '';
  position: absolute;
  top: 5px;
  right: 5px;
  width: 12px;
  height: 12px;
  background: #ef4444;
  border-radius: 50%;
}

.logo-icon i {
  font-size: 28px;
  color: #1e3a8a;
}

.logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.logo-text .collegiate,
.logo-text .international,
.logo-text .school {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}

.contact-info {
  font-size: 14px;
  line-height: 1.8;
}

.contact-info p {
  margin-bottom: 5px;
}

.contact-info .phone {
  margin-top: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.3s ease;
}

.contact-info .phone:hover {
  color: #fbbf24;
}

.accreditation-logos {
  display: flex;
  gap: 20px;
  align-items: center;
}

.logo-badge {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  border: 3px solid white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  font-weight: bold;
  cursor: pointer;
}

.ib-logo {
  font-size: 24px;
}

.ib-logo .world-school {
  font-size: 8px;
  margin-top: -5px;
}

.neasc-logo {
  background: white;
  color: #ef4444;
}

.neasc-text {
  font-size: 16px;
  letter-spacing: 1px;
}

/* Middle Section */
.footer-middle {
  display: flex;
  flex: 1.2;
  flex-direction: column;
  gap: 30px;
  align-items: center;
}

.nav-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.nav-btn {
  padding: 12px 35px;
  background: transparent;
  border: 2px solid white;
  color: white;
  text-decoration: none;
  font-weight: bold;
  font-size: 16px;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  border-radius: 5px;
  position: relative;
  overflow: hidden;
}

.nav-btn:hover {
  background: white;
  color: #1e3a8a;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.nav-btn.active {
  background: white;
  color: #1e3a8a;
}

.footer-links {
  display: flex;
  gap: 80px;
  width: 100%;
  justify-content: center;
}

.links-column h4 {
  font-size: 12px;
  letter-spacing: 1.5px;
  margin-bottom: 15px;
  font-weight: 600;
}

.links-column ul {
  list-style: none;
  padding: 0;
}

.links-column ul li {
  margin-bottom: 10px;
}

.links-column ul li a {
  color: white;
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.links-column ul li a:hover {
  color: #fbbf24;
  transform: translateX(5px);
}

.social-links i {
  font-size: 14px;
}

/* Right Section */
.footer-right {
  display: flex;
  flex: 1;
  align-items: flex-start;
}

.map-container {
  width: 100%;
  height: 300px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  position: relative;
}

.map-iframe {
  width: 100%;
  height: 100%;
  display: block;
}

.map-placeholder {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.map-placeholder i {
  font-size: 48px;
  color: rgba(255, 255, 255, 0.5);
}

.map-placeholder p {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

/* Bottom Bar */
.footer-bottom {
  background: rgba(0, 0, 0, 0.2);
  padding: 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.bottom-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.copyright {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.terms-link {
  color: white;
  text-decoration: underline;
  font-size: 13px;
  transition: color 0.3s ease;
}

.terms-link:hover {
  color: #fbbf24;
}

.credits {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.credits strong {
  color: white;
  font-weight: 600;
}

/* Ripple Effect */
.ripple {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  transform: scale(0);
  animation: ripple-animation 0.6s ease-out;
  pointer-events: none;
}

@keyframes ripple-animation {
  to {
    transform: scale(2);
    opacity: 0;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .footer-container {
    flex-wrap: wrap;
  }

  .footer-left,
  .footer-middle {
    flex: 1 1 48%;
  }

  .footer-right {
    flex: 1 1 100%;
  }
}

@media (max-width: 768px) {
  .footer-container {
    flex-direction: column;
    text-align: center;
  }

  .footer-left,
  .footer-middle,
  .footer-right {
    width: 100%;
    align-items: center;
  }

  .footer-links {
    flex-direction: column;
    gap: 24px;
  }

  .links-column ul li a {
    justify-content: center;
  }

  .nav-buttons {
    flex-direction: column;
    width: 100%;
  }

  .nav-btn {
    width: 100%;
    max-width: 280px;
  }

  .map-container {
    height: 220px;
  }
}



.social-icons {
  display: flex;
  gap: 12px;
  margin-top: 10px;
}

.social-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
}

.social-icon:hover {
  background: white;
  color: #c02131;
  border-color: white;
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
}

.social-icon i {
  font-size: 16px;
}

.school-name {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}

@media (max-width: 480px) {
  .footer-container {
    padding: 0 16px;
    gap: 20px;
  }

  .nav-btn {
    font-size: 14px;
    padding: 10px 20px;
  }
}

</style>